import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Pagination,
  Typography,
  Autocomplete,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { parseDate, getLocalTimeZone } from '@internationalized/date';
import dayjs from "dayjs";
import DatePickerField from "../../common/DatePickerDashboard";
import { getpolicyReportDataAction } from '../../redux/actions/reportActions';
import { useDispatch, useSelector } from 'react-redux';

const poppinsFont = {
  fontFamily: '"Poppins", sans-serif',
};

const PolicyTable = () => {
  const contentRef = useRef();
  const dispatch = useDispatch();
  const policyReportData = useSelector((state) => state.allReportReducer.policyReportData);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const agentOptions = ["Agent 1", "Agent 2", "Agent 3"];
  const locationOptions = ["Location A", "Location B", "Location C"];

  const getToday = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
  const [toDate, setToDate] = useState(dayjs());

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const handleSearch = () => {
    const searchData = {
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      location: selectedLocation || '',
      agent: selectedAgent || '',
    };
    dispatch(getpolicyReportDataAction(searchData, page, rowsPerPage));
  };

  const handleReset = () => {
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setFromDate(dayjs().startOf('month'));
    setToDate(dayjs());
    setSelectedLocation('');
    setSelectedAgent('');
    setFieldErrors({});
    setTouchedFields({});
    setPage(0);
    dispatch(getpolicyReportDataAction({}, 0, rowsPerPage));
  };

  const handleDownload = () => {
    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 1000);
  };

  useEffect(() => {
    setPage(0);
    handleSearch();
  }, [rowsPerPage]);

  useEffect(() => {
    handleSearch();
  }, [page]);

  const data = policyReportData?.content || [];
  const totalElements = policyReportData?.totalElements || 0;

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{ fontFamily: '"Poppins", sans-serif', margin: "30px 40px 0px 65px" }}
        gutterBottom
      >
        Policy Report
      </Typography>
      <Box sx={{ paddingLeft: 8, mr: 8, mt: 4 }} ref={contentRef}>
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            justifyContent: "flex-start",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ width: "225px", height: "50px", marginTop: "-30px" }}>
            <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>From</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="fromDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ width: "225px", height: "50px", marginTop: "-30px" }}>
            <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>To</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="toDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ width: "225px", height: "50px", marginTop: "-30px" }}>
            <FormControl sx={{ width: "225px" }}>
              <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>Select Location</Typography>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                size="small"
                sx={{
                  height: "56px",
                  "& .MuiInputBase-root": { height: "56px", display: "flex", alignItems: "center" },
                }}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locationOptions.map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ width: "225px", height: "50px", marginTop: "-30px" }}>
            <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>Select Agent</Typography>
            <Autocomplete
              options={agentOptions}
              value={selectedAgent}
              onChange={(event, newValue) => setSelectedAgent(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    height: '56px',
                    '& .MuiInputBase-root': { height: '56px' },
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <Button
              onClick={handleSearch}
              className="btn-primary"
              sx={{
                textTransform: "none", marginTop: "30px",
                fontFamily: '"Poppins", sans-serif', fontSize: "14px",
              }}
              disabled={!formData.fromDate || !formData.toDate}
            >
              Search
            </Button>
          </Box>

          <Box>
            <Button
              onClick={handleReset}
              className="btn-secondary"
              sx={{
                textTransform: "none", marginTop: "30px",
                fontFamily: '"Poppins", sans-serif', fontSize: "14px",
              }}
            >
              Reset
            </Button>
          </Box>

          <Box sx={{ marginLeft: "auto" }}>
            <Button
              onClick={handleDownload}
              sx={{
                backgroundColor: "white", color: "black",
                border: "2px solid #FFAE00",
                width: 170, height: 57, marginTop: "30px",
                fontFamily: '"Poppins", sans-serif', fontSize: "14px",
              }}
              disabled={data.length === 0 || isDownloading}
              startIcon={<DownloadIcon />}
            >
              {isDownloading ? 'Downloading...' : 'Download All'}
            </Button>
          </Box>
        </Box>

        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F6C21E' }}>
                <TableCell><strong>Policy Type</strong></TableCell>
                <TableCell><strong>Line of Business</strong></TableCell>
                <TableCell><strong>Company</strong></TableCell>
                <TableCell><strong>Office Location</strong></TableCell>
                <TableCell><strong>Policy Count</strong></TableCell>
                <TableCell><strong>Total Premium</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? (
                data.map((row, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f4f4f4' : '#fff' }}>
                    <TableCell>{row.policyType || '-'}</TableCell>
                    <TableCell>{row.lineOfBusiness || '-'}</TableCell>
                    <TableCell>{row.company || '-'}</TableCell>
                    <TableCell>{row.officeLocation || '-'}</TableCell>
                    <TableCell>{row.policyCount || 0}</TableCell>
                    <TableCell>{row.totalPremium ? `$${row.totalPremium.toFixed(2)}` : '-'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ width: '100%', padding: '16px 0' }}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ fontSize: '12px', marginRight: '8px' }}>
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                size="small"
                sx={{ fontSize: '12px', minWidth: '80px' }}
              >
                {[5, 10, 15, 25, 50, 100].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Pagination
              count={Math.ceil(totalElements / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              size="small"
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default PolicyTable;
